// bomsApi.ts — products (drone models, chargers, any finished good) and their
// parts lists (bills of material). Every API-shape assumption for /boms lives
// here; shapes are read off api/src/parts/boms-router.js and boms-service.js.
//
// A product's key is what the URL carries: its model code for a drone (F021),
// its catalog id for anything else. `bomPath` builds the page link from it.
//
// Numerics arrive as strings (quantity_per "4.00", labor_minutes "90.00").
// labor_rate_cents is ABSENT from the payload unless the caller holds
// can_financial, so it is optional, not nullable-only.

import { useApi } from '../apiHelpers';

export interface Capacity {
  /** null when no line is tagged: one build, everything counts. */
  variant: string | null;
  /** Good units: raw_builds less the failure margin, floored. The figure shown first. */
  builds: number;
  /** What the shelf holds, before the margin. Shown one level down. */
  raw_builds: number;
  limited_by: { line_id: number; key: string; name: string } | null;
}

export interface BomHeader {
  id: string;
  output_part_id: number;
  label: string | null;
  is_active: boolean;
  labor_minutes: string | null;
  labor_rate_cents?: number | null;
  overhead_percent: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  model_code?: string | null;
  sku?: string;
  line_count?: number;
}

/** One part that can fill a family line. Counted members make up the line's stock. */
export interface BomMember {
  part_catalog_id: number;
  sku: string;
  base_name: string | null;
  variant_detail: string | null;
  display_name: string | null;
  stock: number;
  counted: boolean;
  not_counted_reason: 'retired' | 'prototype' | null;
}

export interface BomLine {
  id: number;
  bom_id: string;
  part_family_id: number | null;
  part_catalog_id: number | null;
  quantity_per: string;
  traced: boolean;
  variant: string | null;
  notes: string | null;
  family_key: string | null;
  family_label: string | null;
  sku: string | null;
  display_name: string | null;
  item_is_active: boolean | null;
  stock: number;
  target_key: string;
  target_name: string;
  builds: number;
  /** Family lines only. Counted members come first. */
  members?: BomMember[];
}

export interface BomDetail {
  bom: BomHeader;
  lines: BomLine[];
  capacity: Capacity[];
  /** The margin the capacity was computed with. */
  failure_margin_percent: number;
}

export interface ProductSummary {
  id: number;
  sku: string;
  /** The drone serial prefix. null for a charger, a grabber, any non-drone product. */
  model_code: string | null;
  /** model_code, else the id as a string. What /parts/boms/:key takes. */
  product_key: string;
  description: string | null;
  name: string | null;
  display_name: string | null;
  is_active: boolean;
  bom_id: string | null;
  bom_label: string | null;
  drone_count: number;
  revision_count: number;
  line_count: number;
  /** Whether the dashboard's build capacity card shows this model. */
  on_dashboard: boolean;
  capacity: Capacity[];
  /** The active parts list's distinct line variants, sorted. Empty when it has none. */
  variants: string[];
}

export interface ProductInfo {
  id: number;
  sku: string;
  model_code: string | null;
  product_key: string;
  description: string | null;
  name: string | null;
  display_name: string | null;
  is_active: boolean;
  drone_count: number;
}

export interface ProductDetail {
  product: ProductInfo;
  revisions: BomHeader[];
  active: BomDetail | null;
}

/** The page for a product's parts list. */
export const bomPath = (key: string): string => `/parts/boms/${encodeURIComponent(key)}`;

/** What people call the product: its name, else its code, else its SKU. */
export function productName(p: { display_name: string | null; name: string | null; description?: string | null; model_code: string | null; sku: string }): string {
  return p.display_name || p.name || p.description || p.model_code || p.sku;
}

/** The short handle a heading or a chip shows: the model code for a drone, the SKU otherwise. */
export const productCode = (p: { model_code: string | null; sku: string }): string => p.model_code || p.sku;

export interface NewLineInput {
  part_family_id?: number;
  part_catalog_id?: number;
  quantity_per: number;
  traced?: boolean;
  variant?: string | null;
  notes?: string | null;
}

export interface LinePatch {
  part_family_id?: number;
  part_catalog_id?: number;
  quantity_per?: number;
  traced?: boolean;
  variant?: string | null;
  notes?: string | null;
}

export interface HeaderPatch {
  label?: string | null;
  notes?: string | null;
  labor_minutes?: number | null;
  overhead_percent?: number | null;
  labor_rate_cents?: number | null;
}

export function useBomsApi() {
  const api = useApi();
  return {
    /** Every product with a parts list or a model code. Also the sales, work-order and Assembly pickers. */
    listProducts: (): Promise<ProductSummary[]> => api.get('/boms/products'),

    getProduct: (key: string): Promise<ProductDetail> =>
      api.get(`/boms/products/${encodeURIComponent(key)}`),

    getBom: (bomId: string): Promise<BomDetail> => api.get(`/boms/${bomId}`),

    /** On a product with no list this starts its first one. */
    startRevision: (key: string, label?: string): Promise<BomDetail> =>
      api.post(`/boms/products/${encodeURIComponent(key)}/revisions`, {
        body: label ? { label } : {},
      }),

    patchHeader: (bomId: string, patch: HeaderPatch): Promise<BomDetail> =>
      api.patch(`/boms/${bomId}`, { body: patch }),

    addLine: (bomId: string, input: NewLineInput): Promise<BomDetail> =>
      api.post(`/boms/${bomId}/lines`, { body: input }),

    patchLine: (lineId: number, patch: LinePatch): Promise<BomDetail> =>
      api.patch(`/boms/lines/${lineId}`, { body: patch }),

    deleteLine: (lineId: number): Promise<BomDetail> => api.delete(`/boms/lines/${lineId}`),

    /** `ids: null` means nobody has picked: the dashboard shows every product with parts. */
    getDashboardProducts: (): Promise<{ ids: number[] | null }> =>
      api.get('/boms/dashboard-products'),

    setDashboardProducts: (ids: number[] | null): Promise<{ ids: number[] | null }> =>
      api.put('/boms/dashboard-products', { body: { ids } }),

    /** The failure margin, percent. 5 until an admin sets it. */
    getFailureMargin: (): Promise<{ percent: number }> => api.get('/boms/failure-margin'),

    setFailureMargin: (percent: number): Promise<{ percent: number }> =>
      api.put('/boms/failure-margin', { body: { percent } }),
  };
}

// ---------------------------------------------------------------------------
// Reading helpers
// ---------------------------------------------------------------------------

/** The server's error code, when it sent one ({ error: { code } }). */
export function bomErrorCode(err: unknown): string | null {
  const data = (err as { data?: { error?: { code?: string } } } | null)?.data;
  return data?.error?.code ?? null;
}

export function bomErrorStatus(err: unknown): number | null {
  const status = (err as { status?: number } | null)?.status;
  return typeof status === 'number' ? status : null;
}

/**
 * A sentence for the person. The server's own message wins where it sent one;
 * otherwise the code says what to do. A bare `{ error: { code } }` makes the
 * fetch helper build "[object Object]" as the message, so that is never shown.
 */
export function bomErrorMessage(err: unknown, fallback = 'That did not save. Try again.'): string {
  const data = (err as { data?: { error?: { message?: string } } } | null)?.data;
  const serverMessage = data?.error?.message;
  if (serverMessage) return serverMessage;
  switch (bomErrorCode(err)) {
    case 'bom_inactive':
      return 'This is an older revision and cannot be changed. Go back to the current list.';
    case 'duplicate_line':
      return 'This part is already on the list for that build. Change its quantity instead.';
    case 'financial_required':
      return 'Only people with the financial role can change the hourly rate.';
    case 'revision_conflict':
      return 'Someone else just started a new revision. Reload to see it.';
    case 'invalid_quantity':
      return 'Per unit needs a number above zero.';
    case 'model_as_line':
      return 'A drone model cannot be a part of its own list.';
    case 'not_a_product':
      return 'Only a finished product gets a parts list.';
    case 'unknown_product':
      return 'That product has no parts list, so the dashboard cannot show it.';
    case 'invalid_margin':
      return 'The failure margin is a percent from 0 to 99.99, with at most two decimals.';
    case 'invalid_target':
      return 'Pick a part family or a specific part.';
    case 'invalid_variant':
      return 'A build tag is lower case letters, digits and underscores, such as pyro.';
    default: {
      const message = (err as { message?: string } | null)?.message;
      return message && !message.includes('[object') && !/^HTTP \d/.test(message)
        ? message
        : fallback;
    }
  }
}

/** "4.00" → "4", "0.50" → "0.5". Display only; the wire keeps the string. */
export function formatQty(q: string | number | null | undefined): string {
  if (q === null || q === undefined || q === '') return '—';
  const n = typeof q === 'number' ? q : Number(q);
  if (!Number.isFinite(n)) return String(q);
  return String(parseFloat(n.toFixed(4)));
}

const KNOWN_VARIANTS: Record<string, string> = {
  standard: 'Standard',
  pyro: 'Pyro',
  night_ir: 'Night IR',
};

/** The reading for a build tag. `null` is the untagged build. */
export function variantLabel(variant: string | null, untagged = 'Builds today'): string {
  if (variant === null) return untagged;
  if (KNOWN_VARIANTS[variant]) return KNOWN_VARIANTS[variant];
  const spaced = variant.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** What a line's Variant cell reads: null means the part is in every build. */
export function lineVariantLabel(variant: string | null): string {
  return variant === null ? 'All builds' : variantLabel(variant);
}

/** Free text → the server's tag shape (lower case, digits, underscores). */
export function toVariantTag(text: string): string {
  return text.trim().toLowerCase().replace(/[\s-]+/g, '_').replace(/[^a-z0-9_]/g, '');
}
